import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { getActiveSessions } from '../../services/apiService';
import { format } from 'date-fns';

/**
 * ActiveSessionsDialog component
 * Displays active sessions: one session per source IP, listed by IP.
 * Authenticated users show username; unauthenticated show as Guest.
 */
const ActiveSessionsDialog = ({ open, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getActiveSessions();
      if (response && response.success) {
        setSessions(response.sessions || []);
        setLastUpdated(new Date());
      } else {
        setError('Failed to fetch active sessions');
      }
    } catch (err) {
      console.error('Error fetching active sessions:', err);
      setError(err.message || 'Failed to fetch active sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchSessions();
      // Auto-refresh every 10 seconds when dialog is open
      const interval = setInterval(fetchSessions, 10000);
      return () => clearInterval(interval);
    }
  }, [open]);

  const formatTimeAgo = (seconds) => {
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const formatDate = (timestamp) => {
    try {
      return format(new Date(timestamp), 'MMM d, yyyy h:mm:ss a');
    } catch (err) {
      return 'Invalid date';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <ComputerIcon />
            <Typography variant="h6">Active Sessions (by source IP)</Typography>
            {sessions.length > 0 && (
              <Chip
                label={`${sessions.length} IP${sessions.length !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {lastUpdated && (
              <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                Updated: {format(lastUpdated, 'h:mm:ss a')}
              </Typography>
            )}
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={fetchSessions}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && sessions.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : sessions.length === 0 ? (
          <Alert severity="info">
            No active sessions found.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Source IP</strong></TableCell>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Last Activity</strong></TableCell>
                  <TableCell><strong>First Seen</strong></TableCell>
                  <TableCell><strong>Requests</strong></TableCell>
                  <TableCell><strong>Paths Accessed</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session, index) => (
                  <TableRow key={session.ip} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ComputerIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {session.ip}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {session.username || 'Guest'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={session.authenticated ? (session.userRole || 'Authenticated') : 'Guest'}
                        size="small"
                        color={session.authenticated ? (session.userRole === 'admin' ? 'error' : 'success') : 'default'}
                        variant={session.authenticated ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          {session.lastRequestAgo}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {session.lastRequestFormatted}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {session.firstSeenAgo}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {session.firstSeenFormatted}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={session.requestCount}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              Accessed Paths ({session.pathsCount}):
                            </Typography>
                            {session.paths && session.paths.length > 0 ? (
                              session.paths.slice(0, 10).map((path, idx) => (
                                <Typography key={idx} variant="caption" component="div" sx={{ display: 'block' }}>
                                  {path}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="caption">No paths tracked</Typography>
                            )}
                            {session.paths && session.paths.length > 10 && (
                              <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                ... and {session.paths.length - 10} more
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement="left"
                      >
                        <Chip
                          label={`${session.pathsCount} path${session.pathsCount !== 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                          clickable
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {loading && sessions.length > 0 && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Refreshing...
            </Typography>
          </Box>
        )}

        {sessions.length > 0 && (
          <Box mt={2} p={2} bgcolor="background.default" borderRadius={1}>
            <Typography variant="body2" color="textSecondary">
              <strong>Summary:</strong> {sessions.filter(s => s.authenticated).length} authenticated,{' '}
              {sessions.filter(s => !s.authenticated).length} guest (by source IP).
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
              One session per source IP. Active if requests within the last 15 minutes.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        <Button
          onClick={fetchSessions}
          disabled={loading}
          startIcon={<RefreshIcon />}
          color="primary"
        >
          Refresh
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActiveSessionsDialog;

